<template>

<div id="timeline-wrapper">

  <LoaderComponent v-if="!loaded" />
  <div v-else>

    <!-- Timeline "banner" section -->
    <div id="timeline-search-container">
      <p id="timeline-information">Timeline for <span class="bold">{{ returnValueInLanguage(siteLanguage, dynasty).name }}</span></p>
      <div id="related-container"
        class="related-placement"
        :class="{ 'related-placement': displayPageNavigation,
          'related-no-page-placement': !displayPageNavigation }"
        v-if="resultsExist"
        :dir="rightToLeft ? 'rtl' : 'ltr'">
        <div>
          <p class="related-header"><font-awesome-icon :icon="['fas', 'download']" />{{ this.$i18n.t("gn_download") }}</p>
          <p class="download">
            <a :href="getPrintURL()" target="_blank"><font-awesome-icon :icon="['fas', 'up-right-from-square']" />{{ this.$i18n.t("IslDyn_Timeline_All Events") }}</a>
          </p>
        </div>
      </div>
    </div>

    <BackComponent />

    <div id="top-page-navigation" class="page-navigation-wrapper" v-if="displayPageNavigation">
      <PagesComponent :pageInfo="pageInfo" :navigatePage="navigatePage" :navigatePageDirect="navigatePageDirect"></PagesComponent>
    </div>

    <!-- Main page content -->
    <div id="timeline-container">

      <!-- Labels for sections -->
      <div id="labels-container" v-if="resultsExist"
        :dir="rightToLeft ? 'rtl' : 'ltr'">
        <div id="date-label">{{ this.$i18n.t("gn_databaseSearchFormCriteria_dates") }}</div>
        <div id="country-label">{{ this.$i18n.t("Country") }} | {{ this.$i18n.t("hcr_description") }}</div>
      </div>
      <!-- List of event results -->
      <div id="timeline-wrapper" v-if="resultsExist">
        <div id="timeline" v-for="event in eventsLabeled" :key="event.eventId"
          :dir="rightToLeft ? 'rtl' : 'ltr'">
          <div class="event-container">
            <div class="year"><span>{{ event.beginYear }}</span></div>
            <div class="event">
              <div class="country">{{ returnValueInLanguage(siteLanguage, event.countryName).value }}</div>
              <div class="description" v-html="event.currentDescription.description"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="page-navigation-wrapper" 
      v-if="displayPageNavigation">
      <PagesComponent :pageInfo="pageInfo" :navigatePage="navigatePage" :navigatePageDirect="navigatePageDirect"></PagesComponent>
    </div>
    <div id="padding" v-if="!displayPageNavigation && !lessThan849"></div>

  </div>

</div>

</template>



<script>

import {loadLocaleMessages} from "../i18n.js";
import LoaderComponent from "../components/LoaderComponent.vue";
import BackComponent from "../components/BackComponent.vue";
import PagesComponent from "../components/PagesComponent.vue";
import media from "../mixins/media.js";
import language from "../mixins/language.js";
import pageDisplay from "../mixins/pageDisplay.js";
export default {
  name: "TimelineResults",
  props: ["siteLanguage"],
  components: {
    "LoaderComponent": LoaderComponent,
    "BackComponent": BackComponent,
    "PagesComponent": PagesComponent
  },
  mixins: [media, language, pageDisplay],
  data() {
    return {
      id: this.$route.params.id,
      lessThan849: window.matchMedia("(max-width: 849px)").matches,
      loaded: false,
      events: [],
      dynasty: "",
      resultsExist: true,
        displayPageNavigation: false,
      // Path, current/last page, total pages, etc.
      pageInfo: {},
      // Links to first/last/next/previous pages
      pages: {},
      rightToLeft: false,
      mwnf_website: process.env.VUE_APP_MWNF_WEBSITE,
      dia_website: process.env.VUE_APP_DIA_WEBSITE,
    }
  },
  computed: {
    // Add A.D. or B.C. labels to years
    eventsLabeled: function() {
      let events = this.events;
      let keys = Object.keys(events);
      for (let i = 0; i < keys.length; i++) {
        if (Math.sign(events[keys[i]].beginYear) === 1) {
          events[keys[i]].beginYear = `${events[keys[i]].beginYear} A.D.`;
        } else if (Math.sign(events[keys[i]].beginYear) === -1) {
          events[keys[i]].beginYear = `${events[keys[i]].beginYear} B.C.`;
        }
        events[keys[i]].currentDescription = this.returnValueInLanguage(
          this.siteLanguage, events[keys[i]].description);
      }
      return events;
    },
  },
  watch: {
    "$route" () {
      this.getEvents();
    },
    "siteLanguage" () {
      this.setRightToLeft(this.siteLanguage);
      loadLocaleMessages(this.siteLanguage);
    },
  },
  methods: {
    // Prop used to set conditional dir
    setRightToLeft: function(language) {
      if (language === "ar") {
        this.rightToLeft = true;
      } else {
        this.rightToLeft = false;
      }
    },
    getPrintURL: function() {
      return process.env.VUE_APP_API_ENDPOINT + `/events?d=${this.id}&langId=${this.siteLanguage}&ef=html`;
    },
    getEvents: function() {
      let url = process.env.VUE_APP_API_ENDPOINT + `/events?d=${this.id}`;
      if (this.$route.query.page) {
        let page = `&page=${this.$route.query.page}`;
        url += page;
      }
      return axios({ method: "GET", "url": url }).then(result => {
        this.events = result.data.data;
        this.dynasty = this.events[0].dynastyName;
        this.pageInfo = result.data.meta;
        this.pages = result.data.links;
        this.setResultsExist();
        this.setDisplayPageNavigation();
        this.loaded = true;
      }, error => {
        this.$router.replace({ name: "error" });
        console.error(error);
      });
    },
    setResultsExist: function() {
      if (Object.entries(this.events).length === 0) {
        this.resultsExist = false;
      } else {
        this.resultsExist = true;
      }
    },
    // Navigate to appropriate page (first/last/next/previous)
    navigatePage: function(page) {
      let url = this.pages[page];
      return axios({ method: "GET", "url": url }).then(result => {
        this.results = result.data.items;
        this.pageInfo = result.data.meta;
        this.pages = result.data.pages;
        let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
        let updateQuery = currentQuery;
        updateQuery["page"] = this.pageInfo["current_page"];
        this.$router.push({ name: "timeline", query: updateQuery });
      }, error => {
        console.error(error);
      });
    },
    // Navigate directly to a page by clicking a number
    navigatePageDirect: function(page) {
      let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
      let updateQuery = currentQuery;
      updateQuery["page"] = page;
      this.$router.push({ name: "timeline", query: updateQuery });
    },
  },
  created() {
    this.getEvents();
  },
  mounted () {
    this.setRightToLeft(this.siteLanguage);
    loadLocaleMessages(this.siteLanguage);
  }
}

</script>



<style scoped lang="scss">

.bold {
  font-weight: bold;
}

#timeline-wrapper {
  width: 100%;
  background-color: white;
}
  #timeline-wrapper .fas {
    font-size: 75%;
    padding-right: 4px;
  }

.loader-container {
  min-height: 500px;
}

#timeline-search-container {
  @include flex-center;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  background-color: $theme-medium;
  color: $text-color;
  padding: 20px 50px;
}
  #timeline-information {
    font-size: 110%;
    text-transform: uppercase;
  }
  #search-fields {
    @include flex-center;
    margin-top: 15px;
  }
    #select-container {
      @include flex-center;
    }
    #search-fields span {
      padding: 4px 4px 4px 0;
      margin-right: 10px;
    }
    #search-fields select {
      padding: 4px; 
      margin-right: 20px;
    }
    #country-go-wrapper {
      @include flex-center;
      justify-content: flex-start;
    }
    #results-button button {
      @include button-styles;
      background-color: $theme-light;
      color: $text-color;
      padding: 4px 15px;
      border-radius: 100px;
    }

#related-container {
  width: 25%;
  position: absolute;
  padding: 20px 20px 40px 20px;
  background-color: $theme-medium-dark;
  color: $text-color;
}
  // 100% of timeline search container
  // + 50px for back button height
  // + 65px for navigation height
  // + 34px for labels height
  // To line up related content box with year/country
  .related-placement {
    top: calc(100% + 50px + 65px + 34px);
    right: 0;
  }
    // No navigation height if results are only one page
    // For some reason, the 7px is needed to line it up?
    .related-no-page-placement {
      top: calc(100% + 50px + 7px + 34px);
      right: 0;
    }
  #related-container a {
    color: $text-color;
    text-decoration: none;
  }
  .related-header {
    font-size: 110%;
    font-weight: bold;
    text-decoration: underline;
    margin: 30px 0 10px 0;
  }
  .download {
    cursor: pointer;
  }
    #timeline-wrapper .fa-spinner {
      font-size: 100%;
      padding-right: 0;
    }
    .download .fa-spinner {
      animation-name: spin;
      animation-duration: 2s;
      animation-timing-function: linear;
      animation-iteration-count: infinite;
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

#back {
  @include flex-center;
  justify-content: flex-start;
  height: 50px;
  padding: 0 50px;
}
  #back .fas {
    font-size: 75%;
    padding-right: 4px;
  }
  #back button {
    @include button-styles;
    padding: 2px;
    font-weight: bold;
  }

#top-page-navigation #page-navigation-container {
  margin-top: 0;
  margin-bottom: 30px;
}

#timeline-container {
  min-height: 200px;
  width: 100%;
  background-color: white;
  padding: 0 50px 30px 50px;
}

#labels-container {
  @include flex-center;
  width: 75%;
  margin-bottom: 20px;
  font-size: 110%;
}
  #date-label {
    width: 30%;
    margin-right: 2%;
  }
  #country-label {
    width: 70%;
  }

#timeline {
  width: 75%;
}
  .event-container {
    @include flex-center;
    width: 100%;
    justify-content: flex-start;
    align-items: flex-start;
  }
    .year {
      width: 30%;
      margin-right: 2%;
      border-top: 2px solid $theme-medium-dark;
    }
      .year span {
        background-color: $theme-medium-dark;
        color: $text-color;
        padding: 2px 10px;
      }
    .event {
      width: 70%;
    }
      .country {
        letter-spacing: 0.5px;
        background-color: $theme-medium-dark;
        color: $text-color;
        padding: 2px 10px;
      }
      .description {
        padding: 15px 0 25px 0;
      }

// Prevent margin collapse
.page-navigation-wrapper {
  @include flex-center;
}
#page-navigation-container {
  background-color: white;
}
#padding {
  display: inline-block;
  // Match height of page navigation container
  margin-bottom: 65px;
}

@media only screen and (max-width: 1199px) {
  #timeline-search-container {
    padding: 12px 50px;
  }
  #labels-container {
    font-size: 105%;
  }
}

@media only screen and (max-width: 974px) {
   #timeline-information {
    font-size: 105%;
  }
  .related-header {
    font-size: 105%;
  }
  #date-label {
    width: 20%;
  }
  #country-label {
    width: 80%;
  }
  .year {
    width: 20%;
    border-top: 2px solid white;
  }
  .event {
    width: 80%;
  }
}

@media only screen and (max-width: 849px) {
  #timeline-wrapper {
    position: relative;
  }
  #timeline-search-container {
    position: static;
  }
    #dates-wrapper {
      width: 100%;
      margin-bottom: 10px;
    }
      #search-fields select {
        margin-right: 20px;
      }
    #country-go-wrapper {
      width: 100%;
    }
  #related-container {
    // Height of download button only (no gallery link) is 120px
    // Height of section including gallery link is 185px
    width: calc(100% - (50px * 2));
    padding: 20px 20px 30px 20px;
    margin: 0 50px;
  }
    .related-placement {
      top: auto;
      bottom: 30px;
    }
    .related-header {
      margin-top: 10px;
    }
  #labels-container,
  #timeline {
    width: 100%;
  }
    .year {
      border-top: 2px solid $theme-light;
    }
  #page-navigation-container {
    margin-bottom: 175px;
  }
}

@media only screen and (max-width: 724px) {
  #search-fields span {
    width: 70px;
  }
  #select-container select {
    width: 150px;
  }
  #search-fields select {
    margin-right: 10px;
  }
    #country-go-wrapper select {
      width: 225px;
    }
  .year {
    border-top: 2px solid white;
  }
}

@media only screen and (max-width: 599px) {
  #timeline-search-container {
    padding: 20px 30px;
  }
    #timeline-information {
      display: flex;
      flex-direction: column;
    }
    #select-container select {
      width: 120px;
    }
    #search-fields {
      margin-top: 10px;
    }
      #country-go-wrapper select {
        width: 195px;
      }
  #back {
    padding: 0 30px;
  }
  #related-container {
    // Height of download button only (no gallery link) is 98px
    // Height of section including gallery link is 165px
    width: calc(100% - (30px * 2));
    padding: 10px 20px 20px 20px;
    margin: 0 30px;
  }
  #timeline-container {
    padding: 0 30px 30px 30px;
  }
  #date-label {
    width: 22%;
  }
  #country-label {
    width: 78%;
  }
  .year {
    width: 22%;
  }
  .event {
    width: 78%;
  }
}

@media only screen and (max-width: 499px) {
  #timeline-search-container {
    padding-bottom: 15px;
  }
  #dates-wrapper {
    @include flex-center;
    flex-direction: column;
    margin-bottom: 5px;
  }
    #dates-wrapper span,
    #dates-wrapper select,
    #country-go-wrapper span,
    #country-go-wrapper select {
      width: 100%;
      margin-right: 0;
    }
      #start-date {
        margin-bottom: 5px;
      }
  #country-go-wrapper {
    flex-direction: column;
  }
    #country {
      margin-bottom: 15px;
    }
  #date-label {
    margin-right: 0;
  }
  .year {
    margin-right: 0;
  }
    .year span {
      padding: 2px 5px;
    }
  .country {
    padding: 2px 5px;
  }
}

@media only screen and (max-width: 399px) {
  .year {
    width: 20%;
    background-color: $theme-medium-dark;
    padding: 2px 5px;
    border-top: none;
  }
    .year span {
      padding: 0;
    }
  .event {
    width: 80%;
    margin-left: 5px;
  }
  #country-label {
    margin-left: 5px;
  }
}

@media print {
  #timeline-information,
  .year span,
  .country {
    color: black;
  }
  #labels-container {
    width: 100%;
  }
    #date-label {
      width: 20%;
      margin-right: 0;
    }
    #country-label {
      width: 80%;
    }
  #timeline {
    width: 100%;
  }
    .year {
      width: 20%;
      margin-right: 0;
      border-top: 0;
      border-bottom: 1px solid black;
    }
      .year span {
        background-color: none;
        padding: 0;
      }
    .event {
      width: 80%;
    }
      .country {
        background-color: none;
        padding: 0;
        border-bottom: 1px solid black;
      }
  #search-fields,
  #related-container,
  #back,
  .page-navigation-wrapper {
    display: none;
  }
}

</style>